import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface ConsortiumInvitation {
  id: string;
  consortium_id: string;
  inviter_id: string;
  invitee_id: string;
  invitation_message?: string;
  status: 'pending' | 'accepted' | 'declined';
  created_at: string;
  updated_at: string;
  consortium?: {
    consortium_name: string;
    opportunity_id: string;
    opportunities?: {
      title: string;
      company: string;
    };
  };
  inviter?: {
    name: string;
    company: string;
    image?: string;
  };
}

export const useConsortiumInvitations = () => {
  const [invitations, setInvitations] = useState<ConsortiumInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchInvitations = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('consortium_invitations')
        .select(`
          id,
          consortium_id,
          inviter_id,
          invitee_id,
          invitation_message,
          status,
          created_at,
          updated_at
        `)
        .eq('invitee_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get additional data for each invitation
      const enrichedInvitations = await Promise.all(
        (data || []).map(async (invitation) => {
          // Get consortium info
          const { data: consortiumData } = await supabase
            .from('opportunity_consortiums')
            .select('consortium_name, opportunity_id')
            .eq('id', invitation.consortium_id)
            .single();

          // Get opportunity info separately  
          let opportunityData = null;
          if (consortiumData?.opportunity_id) {
            const { data: oppData } = await supabase
              .from('opportunities')
              .select('title, company')
              .eq('id', consortiumData.opportunity_id)
              .single();
            opportunityData = oppData;
          }

          // Get inviter info  
          const { data: inviterData } = await supabase
            .from('club_members')
            .select('name, company, image')
            .eq('user_id', invitation.inviter_id)
            .single();

          return {
            ...invitation,
            status: invitation.status as 'pending' | 'accepted' | 'declined',
            consortium: consortiumData ? {
              consortium_name: consortiumData.consortium_name,
              opportunity_id: consortiumData.opportunity_id,
              opportunities: opportunityData
            } : undefined,
            inviter: inviterData || undefined
          };
        })
      );

      setInvitations(enrichedInvitations);
    } catch (error) {
      console.error('Error fetching invitations:', error);
      toast.error('Erreur lors du chargement des invitations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, [user]);

  const respondToInvitation = async (
    invitationId: string, 
    response: 'accepted' | 'declined'
  ): Promise<void> => {
    if (!user) {
      toast.error('Vous devez être connecté');
      return;
    }

    try {
      // Update invitation status
      const { error: updateError } = await supabase
        .from('consortium_invitations')
        .update({ 
          status: response,
          updated_at: new Date().toISOString()
        })
        .eq('id', invitationId);

      if (updateError) throw updateError;

      if (response === 'accepted') {
        // Get invitation details to join consortium
        const invitation = invitations.find(inv => inv.id === invitationId);
        if (!invitation) {
          throw new Error('Invitation not found');
        }

        // Add user to consortium
        const { error: joinError } = await supabase
          .from('opportunity_consortiums')
          .insert({
            user_id: user.id,
            opportunity_id: invitation.consortium?.opportunity_id,
            consortium_name: invitation.consortium?.consortium_name,
            role_in_consortium: 'member',
            status: 'active'
          });

        if (joinError) throw joinError;

        toast.success('Invitation acceptée ! Vous avez rejoint le consortium.');
      } else {
        toast.success('Invitation déclinée.');
      }

      // Refresh invitations
      await fetchInvitations();
    } catch (error) {
      console.error('Error responding to invitation:', error);
      toast.error('Erreur lors de la réponse à l\'invitation');
    }
  };

  return {
    invitations,
    loading,
    respondToInvitation,
    refetch: fetchInvitations
  };
};